import type { Team, TeamEntitlementsResponse, User } from '@kangaroo/shared';
import { isPasswordRecoveryEntryRoute } from '../pages/authRouteState';

interface EntryState {
  pathname: string;
  search: string;
  token: string | null;
  user: User | null;
  isBootstrapping: boolean;
  kioskTokenAlreadyExpired: boolean;
  returnToKiosk: boolean;
}

/** Preserve the existing priority: public entries, kiosk/recovery, auth, then workspace gates. */
export function selectEntryRoute(state: EntryState) {
  const { pathname, search } = state;
  if (pathname === '/mobile/billing-return') return 'mobile-billing-return';
  if (pathname === '/demo/session') return 'demo';
  if (pathname.startsWith('/live/')) return 'live-share';
  if (pathname === '/kiosk') return 'kiosk';
  if (state.isBootstrapping) return 'loading';
  if (state.kioskTokenAlreadyExpired) return 'return-kiosk';
  if (isPasswordRecoveryEntryRoute(pathname, search)) return 'password-recovery';
  if (pathname === '/recordings/play') return 'recording';
  if (pathname === '/connect/chatgpt/action') return 'chatgpt-action';
  if (pathname === '/connect/chatgpt') return 'chatgpt-connect';
  if (!state.token || !state.user) return state.returnToKiosk ? 'return-kiosk' : 'signed-out';
  if (pathname.startsWith('/k/')) return 'kiosk-pair';
  if (pathname.startsWith('/s/v1/')) return 'session-scan';
  if (pathname === '/register' && new URLSearchParams(search).has('setupToken'))
    return 'setup-registration';
  return null;
}

export type EntryRoute = NonNullable<ReturnType<typeof selectEntryRoute>>;

export function selectWorkspaceGate(state: {
  user: User;
  currentTeam: Team | undefined;
  currentTeamLoading: boolean;
  entitlements: TeamEntitlementsResponse | undefined;
  entitlementsLoading: boolean;
}) {
  const { user, currentTeam, currentTeamLoading, entitlements, entitlementsLoading } = state;
  if (!user.teamId || currentTeam?.workspaceSetupPending) {
    return user.teamId && currentTeamLoading ? 'loading' : 'setup';
  }
  if (user.teamId && entitlementsLoading && !entitlements) return 'loading';
  if (user.accountDisabled) return 'billing-recovery';
  return 'ready';
}
